import type {GridColDef} from "@mui/x-data-grid-premium";
import {Divider, Stack, Tooltip, Typography} from "@mui/material";
import {NO_VALUE, PART_SEP} from "~/constants";
import {Tag} from "~/components/tag/Tag";
import {AuthorChip} from "~/pages/taxonomy-editor/approval/component/AuthorChip";
import {TAXONOMY_CHANGE_META} from "~/pages/taxonomy-builder-v2/constants/taxonomyChangeMeta";
import type {TaxonomyChangePart, TaxonomyChangeRow} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import {DataGridColumnHeaderInfo} from "~/components/data-grid/DataGridColumnHeaderInfo";

const textCell = (value: string | undefined, key?: number) => (
    <Typography key={key} variant="body2" noWrap title={value} color={value ? 'text.primary' : 'text.secondary'}>
        {value ?? NO_VALUE}
    </Typography>
);

/**
 * One outlined default tag per part, stacked one per line: the tag shows only the changed levels and a
 * tooltip shows the full path.
 */
const tagsCell = (parts: TaxonomyChangePart[]) => parts.length === 0
    ? textCell(undefined)
    : (
        // One tag per line, stacked.
        <Stack alignItems="flex-start" gap={0.5} sx={{py: 1, minWidth: 0, width: '100%'}}>
            {parts.map((part, i) => (
                <Tooltip
                    key={i}
                    title={part.title}
                    // A path reads best on one line: no width limit, no wrapping.
                    slotProps={{tooltip: {sx: {maxWidth: 'none', whiteSpace: 'nowrap'}}}}
                >
                    {/* Tag doesn't forward a ref, which the tooltip needs. */}
                    <Stack component="span" sx={{maxWidth: '100%', minWidth: 0}}>
                        <Tag color="default" variant="outlined" label={part.label} size="small" sx={{maxWidth: '100%'}}/>
                    </Stack>
                </Tooltip>
            ))}
        </Stack>
    );

/**
 * Draft change columns (design: Change · Before · After · Transactions affected, plus the prototype's
 * Author). The design's "From" column and Discard action are left out: the backend records neither.
 */
export function getYourChangesColumns(): GridColDef<TaxonomyChangeRow>[] {
    return [
        {
            // One tag per change type on the category, with a count when it happened more than once (a merge
            // records a rename per merged category); filtering and sorting use the labels, e.g. "Renamed, Updated".
            // The tags wrap, and the row grows (autoRowHeight), so none is cut off.
            field: 'changes',
            headerName: 'Change',
            width: 200,
            valueGetter: (_, row) => row.changes.map(c => TAXONOMY_CHANGE_META[c.type].label).join(', '),
            renderCell: ({row}) => (
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{py: 1}}>
                    {[...new Set(row.changes.map(c => c.type))].map(type => {
                        const meta = TAXONOMY_CHANGE_META[type];
                        const count = row.changes.filter(c => c.type === type).length;
                        const label = count > 1 ? `${meta.label} ×${count}` : meta.label;
                        return <Tag key={type} solid={false} label={label} color={meta.tag} size="small"/>;
                    })}
                </Stack>
            ),
        },
        {
            field: 'before',
            headerName: 'Before',
            renderHeader: () => (
                <DataGridColumnHeaderInfo
                    title="Before"
                    info="The changed levels. Hover to see the full path."
                />
            ),
            flex: 1.5,
            minWidth: 240,
            valueGetter: (_, row) => row.before.map(p => p.title).join(PART_SEP),
            renderCell: ({row}) => tagsCell(row.before),
        },
        {
            field: 'after',
            headerName: 'After',
            renderHeader: () => (
                <DataGridColumnHeaderInfo
                    title="After"
                    info="The changed levels. Hover to see the full path."
                />
            ),
            flex: 1.5,
            minWidth: 240,
            valueGetter: (_, row) => row.after.map(p => p.title).join(PART_SEP),
            renderCell: ({row}) => tagsCell(row.after),
        },
        {
            // One line per description change (a merge records one per merged category), split by a line.
            field: 'previousDescriptions',
            headerName: 'Previous description',
            flex: 1,
            minWidth: 200,
            valueGetter: (_, row) => row.previousDescriptions.join(PART_SEP),
            renderCell: ({row}) => row.previousDescriptions.length <= 1
                ? textCell(row.previousDescriptions[0])
                : (
                    <Stack divider={<Divider flexItem/>} gap={0.5} sx={{py: 1, width: '100%', minWidth: 0}}>
                        {row.previousDescriptions.map((description, i) => textCell(description, i))}
                    </Stack>
                ),
        },
        {
            field: 'currentDescription',
            headerName: 'Current description',
            flex: 1,
            minWidth: 200,
            renderCell: ({row}) => textCell(row.currentDescription),
        },
        {
            field: 'transactions',
            headerName: 'Transactions affected',
            width: 180,
            type: 'number',
            valueFormatter: (value: number) => value.toLocaleString(),
        },
        {
            field: 'authors',
            headerName: 'Author',
            width: 220,
            sortable: false,
            filterable: false,
            renderCell: ({row}) => row.authors.length === 0
                ? <Typography variant="body2" color="text.secondary">{NO_VALUE}</Typography>
                : (
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{height: '100%', overflow: 'hidden'}}>
                        {row.authors.map(id => <AuthorChip key={id} authorId={id}/>)}
                    </Stack>
                ),
        },
    ];
}
